import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Spinner } from "reactstrap";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";

import { getDashboardSalidasV2, reportTicketPublic } from "../../../helpers/backend_helper";
import PreviewPDF from "../../../helpers/previewPDF";
import SalidaRow from "./SalidaRow";
import { buildKpis, buildRow, LEYENDA, ORDENES, sortRows } from "./monitorHelper";
import "./SalidasDashboardV2.css";

const REFRESH_SECONDS = 60;
const TODOS = "";

const SalidasDashboardV2 = () => {
  const { route_slug } = useParams();

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [stale, setStale] = useState(false);
  const [countdown, setCountdown] = useState(REFRESH_SECONDS);
  const [tab, setTab] = useState("arrive");
  const [ramal, setRamal] = useState(TODOS);
  const [orden, setOrden] = useState("llegada");
  const [abierto, setAbierto] = useState(null);
  const [modalPdf, setModalPdf] = useState({ show: false, url: "" });
  const loadingRef = useRef(false);

  const fetchData = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setIsRefreshing(true);
    try {
      const response = await getDashboardSalidasV2(route_slug);
      // `status === false` es el sentinel de fallo de api_helper.
      if (!response || response.status === false) {
        setStale(true);
        return;
      }
      setData(response);
      setStale(false);
    } catch (error) {
      console.log(error);
      setStale(true);
    } finally {
      loadingRef.current = false;
      setIsRefreshing(false);
      setIsLoading(false);
      setCountdown(REFRESH_SECONDS);
    }
  }, [route_slug]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const intervalId = setInterval(() => setCountdown((prev) => (prev > 0 ? prev - 1 : 0)), 1000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (countdown === 0) fetchData();
  }, [countdown, fetchData]);

  const rows = useMemo(
    () => (data?.salidas ?? []).map((salida) => buildRow(salida, data.today)),
    [data]
  );

  const kpis = useMemo(() => buildKpis(rows), [rows]);

  const visibles = useMemo(
    () => sortRows(ramal === TODOS ? rows : rows.filter((r) => r.ramalId === ramal), orden),
    [rows, ramal, orden]
  );

  const ramales = useMemo(() => {
    const conSalidas = new Set(rows.map((r) => r.ramalId));
    return [
      { id: TODOS, name: "Todos" },
      ...(data?.branches ?? []).filter((b) => conSalidas.has(b.id) || b.id === ramal),
    ];
  }, [data, rows, ramal]);

  const printTicket = async (salidaId) => {
    const config = {
      params: { salida_id: salidaId },
      headers: { Accept: "application/pdf" },
      responseType: "blob",
    };
    const ticket = await reportTicketPublic(config);
    const reader = new FileReader();
    reader.readAsDataURL(ticket);
    reader.onloadend = () => setModalPdf({ show: true, url: reader.result });
  };

  if (isLoading)
    return (
      <div>
        <div className="maintenance-cog-icon text-primary pt-4 text-center">
          <i className="mdi mdi-cog spin-right display-3"></i>
          <i className="mdi mdi-cog spin-left display-4 cog-icon"></i>
          <h3 className="mt-4">Cargando información de la ruta</h3>
          <p className="text-dark">Espere un momento por favor</p>
        </div>
      </div>
    );

  if (!data?.route) return <p className="text-center py-4">No se encontro información de la ruta</p>;

  const salidasDepart = data.salidasDepart ?? [];

  return (
    <div className="mr2">
      <div className="mr2-top">
        <div className="mr2-brand">GoGoTrack</div>
        <div className="mr2-sep" />
        <div className="mr2-title">Salidas · {data.route.name}</div>
        <div className={`mr2-live${stale ? " is-stale" : ""}`}>
          <span className="mr2-live-dot" />
          <span>
            {stale ? "Sin actualizar" : "En vivo"} · corte {dayjs(data.today).format("HH:mm")}
          </span>
        </div>
        <div className="mr2-top-actions">
          <span className="mr2-countdown">Actualiza en {countdown}s</span>
          <Button color="dark" size="sm" onClick={fetchData} disabled={isRefreshing}>
            {isRefreshing ? <Spinner size="sm" className="me-1" /> : <i className="ri-refresh-line me-1"></i>}
            Actualizar
          </Button>
        </div>
      </div>

      <div className="mr2-filters">
        <div className="mr2-seg">
          <button
            type="button"
            className={`mr2-seg-item${tab === "depart" ? " is-active" : ""}`}
            onClick={() => setTab("depart")}
          >
            Por iniciar <span className="mr2-muted">{salidasDepart.length}</span>
          </button>
          <button
            type="button"
            className={`mr2-seg-item${tab === "arrive" ? " is-active" : ""}`}
            onClick={() => setTab("arrive")}
          >
            Por concluir <span className="mr2-muted">{rows.length}</span>
          </button>
        </div>
        {tab === "arrive" && (
          <>
            <div className="mr2-chips">
              <span className="mr2-chips-label">Ramal</span>
              {ramales.map((b) => (
                <button
                  type="button"
                  key={b.id || "todos"}
                  className={`mr2-chip${ramal === b.id ? " is-active" : ""}`}
                  onClick={() => setRamal(b.id)}
                >
                  {b.name}
                </button>
              ))}
            </div>
            <div className="mr2-chips is-end">
              <span className="mr2-chips-label">Ordenar</span>
              {ORDENES.map((o) => (
                <button
                  type="button"
                  key={o.key}
                  className={`mr2-chip${orden === o.key ? " is-active" : ""}`}
                  onClick={() => setOrden(o.key)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {tab === "arrive" && (
        <div className="mr2-kpis">
          {kpis.map((k) => (
            <div className="mr2-kpi" key={k.key}>
              <div className="mr2-kpi-label">
                <span className="mr2-swatch" style={{ background: k.color }} />
                <span>{k.label}</span>
              </div>
              <div className="mr2-kpi-value">
                {k.value} <span className="mr2-kpi-unit">{k.unit}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mr2-body">
        {tab === "arrive" && (
          <div className="mr2-list">
            <div className="mr2-grid mr2-head">
              <div>Unidad</div>
              <div>Ramal</div>
              <div>Tiempos por geocerca · real / programado</div>
              <div className="mr2-right">Desviación</div>
              <div className="mr2-right">Llegada a base</div>
              <div className="mr2-right">Restan</div>
              <div></div>
            </div>
            {visibles.map((row) => (
              <SalidaRow
                key={row.id}
                row={row}
                abierto={abierto === row.id}
                onToggle={() => setAbierto((prev) => (prev === row.id ? null : row.id))}
              />
            ))}
            {!visibles.length && <div className="mr2-card mr2-empty">No se encontraron salidas</div>}
          </div>
        )}

        {tab === "depart" && (
          <div className="mr2-card">
            <table className="table table-sm align-middle">
              <thead className="table-light">
                <tr>
                  <th>Salida</th>
                  <th>Hora</th>
                  <th>Unidad</th>
                  <th>Destino</th>
                  <th className="text-end">Acción</th>
                </tr>
              </thead>
              <tbody>
                {salidasDepart.map((salida) => (
                  <tr key={salida.id}>
                    <td>{salida.no}</td>
                    <td>{salida.time}</td>
                    <td className="fw-semibold">{salida.unit_name}</td>
                    <td>{salida.destination}</td>
                    <td className="text-end">
                      <Button size="sm" color="light" onClick={() => printTicket(salida.id)}>
                        <i className="fa fas fa-print"></i> Imprimir Ticket
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!salidasDepart.length && <div className="mr2-empty">No se encontraron salidas</div>}
          </div>
        )}
      </div>

      {tab === "arrive" && (
        <div className="mr2-legend">
          <span className="mr2-legend-title">Escala de desviación</span>
          {LEYENDA.map((l) => (
            <div className="mr2-legend-item" key={l.label}>
              <span className="mr2-swatch" style={{ background: l.color, width: 9, height: 9 }} />
              <span>{l.label}</span>
            </div>
          ))}
        </div>
      )}

      {modalPdf.show && <PreviewPDF title="Ticket" modalPdf={modalPdf} setModalPdf={setModalPdf} />}
    </div>
  );
};

export default SalidasDashboardV2;
